import React, { useEffect, useId, useState, type ComponentProps, type ReactElement, type ReactNode } from 'react';

import classNames from 'classnames';
import { Calendar } from '@buildoutinc/blueprint-react/ui/Calendar';
import { Popover } from '@buildoutinc/blueprint-react/ui/Popover';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { formatTokens, isValidDate, parseWithFormats } from 'helpers/date_utils';
import { DATE_FORMAT, ISO_DATE_FORMAT } from 'helpers/formatters/date_time_formats';

// Values are stored as ISO_DATE_FORMAT: Rails casts it onto a date column without reinterpreting it, and it is
// what comes back.

// Blueprint's calendar stops five years out, short of a long lease's dates.
const LAST_MONTH = new Date(new Date().getFullYear() + 99, 11);

export function pickedDate(value: string, format?: string) {
  const parsed = parseWithFormats(value, format ? [format, ISO_DATE_FORMAT] : ISO_DATE_FORMAT);

  return isValidDate(parsed) ? parsed : undefined;
}

type PopoverContentProps = ComponentProps<typeof Popover.Content>;

interface Props {
  align?: PopoverContentProps['align'],
  // Set where the control has no label of its own to be associated with -- inside a table cell.
  ariaLabel?: string,
  children?: ReactNode,
  className?: string,
  // The format the trigger shows the picked date in.
  dateFormat?: string,
  disabled?: boolean,
  disabledDays?: ComponentProps<typeof Calendar>['disabled'],
  format?: string,
  id?: string,
  // Shown on the trigger in place of the date, for a value the date alone doesn't describe.
  label?: string | null,
  open?: boolean,
  // Stops clicking the selected day from clearing it.
  required?: boolean,
  saving?: boolean,
  side?: PopoverContentProps['side'],
  // Replaces the form-control trigger, which then has to name itself and show the date.
  trigger?: ReactElement,
  value: string,
  onChange: (value: string) => void,
  onOpenChange?: (open: boolean) => void,
}

// The app's date control: picked from a calendar rather than typed, so a half-finished or
// impossible date can never reach the value, and rendered through a fixed date format rather than
// the browser's locale. The trigger is a real button so a label opens it and a form-wide
// <fieldset disabled> reaches it while a save is in flight.
export default function DatePicker({
  align = 'start',
  ariaLabel,
  children,
  className,
  dateFormat = DATE_FORMAT,
  disabled,
  disabledDays,
  format,
  id,
  label,
  open,
  required,
  saving,
  side,
  trigger,
  value,
  onChange,
  onOpenChange,
}: Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  // A label names the button in place of its text, so the picked date is read out as its description.
  const valueId = useId();

  const setOpen = (next: boolean) => {
    if (open === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  // The popover renders in a portal, outside the form's disabled fieldset, so a calendar left open
  // when Save was pressed stays clickable -- and the pick would be overwritten by the response.
  useEffect(() => {
    if (saving) setOpen(false);
  }, [saving]);

  const selected = pickedDate(value, format);

  return (
    <Popover open={open ?? uncontrolledOpen} onOpenChange={setOpen}>
      <Popover.Trigger
        disabled={disabled}
        render={trigger ?? (
          <button
            aria-describedby={valueId}
            aria-label={ariaLabel}
            className={classNames(
              'form-control d-flex align-items-center justify-content-between text-start',
              className
            )}
            id={id}
            type="button"
          >
            <span className={label || selected ? undefined : 'text-muted'} id={valueId}>
              {label || (selected ? formatTokens(selected, dateFormat) : 'Pick a date')}
            </span>
            <FontAwesomeIcon className="text-muted" icon={faCalendar} />
          </button>
        )}
      />
      <Popover.Content align={align} side={side}>
        <Popover.Body>
          {children}
          <Calendar
            defaultMonth={selected}
            disabled={disabledDays}
            endMonth={LAST_MONTH}
            // defaultMonth is only read on mount, so a date moved to another month while open remounts it there.
            key={selected ? `${selected.getFullYear()}-${selected.getMonth()}` : 'none'}
            mode="single"
            required={required}
            selected={selected}
            // Clicking the selected day again is react-day-picker's clear gesture, and it is the
            // only way to empty a field that cannot be typed into.
            onSelect={(date?: Date) => {
              onChange(date ? formatTokens(date, format ?? ISO_DATE_FORMAT) : '');
              setOpen(false);
            }}
          />
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
}
